(() => {
  'use strict';

  function initializeProduct(product) {
    if (product.dataset.commerceReady === 'true') return;
    product.dataset.commerceReady = 'true';
    const variants = product.querySelector('[data-pulse-variant]');
    const quantity = product.querySelector('[data-pulse-quantity]');
    const add = product.querySelector('[data-pulse-add]');
    const price = product.querySelector('[data-pulse-price]');
    const compare = product.querySelector('[data-pulse-compare]');
    const sku = product.querySelector('[data-pulse-sku]');
    const skuRow = product.querySelector('[data-pulse-sku-row]');
    const availability = product.querySelector('[data-pulse-availability]');
    const imageLink = product.querySelector('[data-pulse-image-link]');
    const mainImage = product.querySelector('.pulse-product-main-image');
    const thumbnails = Array.from(product.querySelectorAll('[data-pulse-thumbnail]'));

    function showImage(data, fullSize) {
      if (!mainImage || !data.imageSrc) return;
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = data.imageSrc;
      mainImage.alt = data.imageAlt || '';
      if (data.imageWidth && data.imageHeight) {
        mainImage.width = Number(data.imageWidth);
        mainImage.height = Number(data.imageHeight);
      }
      if (imageLink) imageLink.href = fullSize || data.imageFull || data.imageSrc;
      thumbnails.forEach((thumbnail) => {
        if (thumbnail.dataset.imageId === data.imageId) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
    }

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        showImage(thumbnail.dataset, thumbnail.href);
      });
    });

    function updateVariant(updateUrl) {
      const option = variants?.selectedOptions[0];
      if (!option) return;
      const data = option.dataset;
      const available = data.available === 'true';
      if (price) price.textContent = data.price || '';
      if (compare) {
        compare.textContent = data.compare || '';
        compare.hidden = !data.compare;
      }
      if (sku) sku.textContent = data.sku || '';
      if (skuRow) skuRow.hidden = !data.sku;
      if (availability) {
        availability.textContent = available ? 'Available to order' : 'Sold out';
        availability.dataset.available = String(available);
      }
      if (add) {
        add.disabled = !available;
        add.textContent = available ? 'Add to cart' : 'Sold out';
      }
      if (quantity) {
        const minimum = Math.max(1, Number(data.min) || 1);
        const increment = Math.max(1, Number(data.step) || 1);
        quantity.min = String(minimum);
        quantity.step = String(increment);
        if (data.max) quantity.max = data.max;
        else quantity.removeAttribute('max');
        let next = Math.max(minimum, Number(quantity.value) || minimum);
        next = minimum + Math.ceil((next - minimum) / increment) * increment;
        if (data.max) next = Math.min(Number(data.max), next);
        quantity.value = String(next);
      }
      showImage(data);
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState(window.history.state, '', url);
      }
    }

    variants?.addEventListener('change', () => updateVariant(true));
    updateVariant(false);
  }

  function initializeCart(cart) {
    const form = cart.querySelector('.pulse-cart-form');
    if (!form || cart.dataset.cartReady === 'true' || typeof fetch !== 'function') return;
    const status = cart.querySelector('[data-pulse-cart-status]');
    const error = cart.querySelector('[data-pulse-cart-error]');
    const errorMessage = cart.querySelector('[data-pulse-cart-error-message]');
    if (!status || !error || !errorMessage || !cart.dataset.cartUpdateUrl) return;
    cart.dataset.cartReady = 'true';
    let updating = false;
    form.addEventListener('submit', async (event) => {
      // Checkout and the native no-JavaScript form keep Shopify's normal flow.
      if (event.submitter?.name !== 'update') return;
      event.preventDefault();
      if (updating) return;
      if (!form.reportValidity()) return;
      const inputs = Array.from(form.querySelectorAll('[data-cart-line-key]'));
      const updates = Object.create(null);
      for (const input of inputs) {
        const quantity = Number(input.value);
        if (!input.dataset.cartLineKey || input.value.trim() === '' || !Number.isSafeInteger(quantity) || quantity < 0) {
          errorMessage.textContent = 'Enter a whole number of zero or more for each quantity.';
          error.hidden = false;
          return;
        }
        if (quantity !== Number(input.dataset.cartOriginalQuantity)) updates[input.dataset.cartLineKey] = quantity;
      }
      if (Object.keys(updates).length === 0) { window.location.assign(form.action); return; }
      const button = event.submitter;
      const buttonText = button.textContent;
      const submitButtons = Array.from(form.querySelectorAll('button[type="submit"],input[type="submit"]')).map(control => ({ control, disabled: control.disabled }));
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      updating = true;
      submitButtons.forEach(({ control }) => { control.disabled = true; });
      button.textContent = 'Updating…';
      inputs.forEach(input => { input.readOnly = true; });
      form.setAttribute('aria-busy', 'true');
      error.hidden = true;
      status.hidden = false;
      status.textContent = 'Updating your cart…';
      try {
        const response = await fetch(cart.dataset.cartUpdateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates }),
          signal: controller.signal
        });
        const result = await response.json();
        if (!response.ok) {
          errorMessage.textContent = typeof result.description === 'string' ? result.description : 'Your cart could not be updated. Refresh it and check the quantities.';
          error.hidden = false;
          status.hidden = true;
          return;
        }
        if (!Array.isArray(result.items)) throw new Error('Unconfirmed cart response');
        status.textContent = 'Cart updated. Loading your totals…';
        // Navigate with GET after the API save, avoiding the failed native POST return.
        window.location.assign(form.action);
      } catch {
        errorMessage.textContent = 'We could not confirm your update. Refresh your cart before trying again.';
        error.hidden = false;
        status.hidden = true;
      } finally {
        clearTimeout(timeout);
        updating = false;
        submitButtons.forEach(({ control, disabled }) => { control.disabled = disabled; });
        button.textContent = buttonText;
        inputs.forEach(input => { input.readOnly = false; });
        form.removeAttribute('aria-busy');
      }
    });
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-pulse-product]').forEach(initializeProduct);
    scope.querySelectorAll('[data-pulse-cart]').forEach(initializeCart);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
